import { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface MobileViewportOptimizerProps {
  children: React.ReactNode;
}

export function MobileViewportOptimizer({ children }: MobileViewportOptimizerProps) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      // Optimize viewport for mobile devices
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        );
      }

      // Add mobile-specific body classes
      document.body.classList.add('mobile-optimized');
      
      // Prevent zoom on form inputs
      const style = document.createElement('style');
      style.textContent = `
        @media screen and (max-width: 768px) {
          input[type="text"],
          input[type="email"],
          input[type="password"],
          input[type="number"],
          input[type="tel"],
          textarea,
          select {
            font-size: 16px !important;
            transform: none !important;
          }
          
          /* Prevent horizontal scroll */
          body {
            overflow-x: hidden;
          }
          
          /* Improve touch targets */
          button,
          [role="button"],
          .clickable {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Smooth scrolling on iOS */
          * {
            -webkit-overflow-scrolling: touch;
          }
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.body.classList.remove('mobile-optimized');
        document.head.removeChild(style);
      };
    }
  }, [isMobile]);

  return (
    <div className={isMobile ? 'mobile-viewport-optimized' : ''}>
      {children}
    </div>
  );
}

// Mobile-specific layout wrapper
export function MobileResponsiveContainer({ 
  children, 
  className = "",
  noPadding = false 
}: { 
  children: React.ReactNode;
  className?: string;
  noPadding?: boolean;
}) {
  const isMobile = useIsMobile();
  
  const containerClasses = [
    'w-full',
    isMobile && !noPadding ? 'px-4 py-2' : '',
    isMobile ? 'safe-area-left safe-area-right' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {children}
    </div>
  );
}

// Touch-optimized button wrapper
export function TouchOptimizedButton({ 
  children, 
  className = "",
  ...props 
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  
  const buttonClasses = [
    className,
    isMobile ? 'touch-manipulation min-h-[44px] min-w-[44px]' : '',
  ].filter(Boolean).join(' ');

  return (
    <button className={buttonClasses} {...props}>
      {children}
    </button>
  );
}